import React, { useCallback, useEffect, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { giftCardCategory } from "@/utils/giftCardUtils";
import { Category } from "@/dto/category";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import { ROUTE_SERVICES, ROUTE_SHOP } from "@/utils/constants";

interface SecondaryServicesNavigationBarProps {
  displayCategories: Category[];
  moreCategories: Category[];
  retailCategories: Category[];
  serviceCategories: Category[];
  scanToOrderInfo?: {
    tableId: string;
    locationId: string;
    menuId?: string;
  };
  isNotAcceptingOrder: boolean;
}

export const SecondaryServicesNavigationBar = ({
  displayCategories,
  moreCategories,
  retailCategories,
  serviceCategories,
  scanToOrderInfo,
  isNotAcceptingOrder,
}: SecondaryServicesNavigationBarProps) => {
  const t = useTranslations("NavigationBar");
  const [currentCategory, setCurrentCategory] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const searchParams = useSearchParams();
  const menuName = searchParams?.get("menu");

  useEffect(() => {
    const param = window.location.hash.substring(1);
    setCurrentCategory(param);
  }, []);

  const handleScroll = useCallback(() => {
    const mainElement = document.querySelector("main");
    if (mainElement && mainElement.scrollTop > 0) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  }, [setIsScrolled]);

  useEffect(() => {
    const mainElement = document.querySelector("main");
    if (mainElement) {
      mainElement.addEventListener("scroll", handleScroll);

      return () => {
        mainElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  const pathName = usePathname();
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [filteredMoreCategories, setFilteredMoreCategories] = useState<
    Category[]
  >([]);
  useEffect(() => {
    if (!displayCategories) return;

    setFilteredCategories(
      displayCategories.filter((c: Category) => c.id !== giftCardCategory.id)
    );
  }, [displayCategories, displayCategories.length, giftCardCategory]);

  useEffect(() => {
    if (!moreCategories) return;
    if (pathName?.includes("/services") || pathName?.includes("/shop")) return;
    setFilteredMoreCategories(moreCategories);
  }, [moreCategories, pathName]);

  useEffect(() => {
    setCurrentCategory(""); // make sure when page changes, there is no category selected
    let MAX_CATEGORIES_DISPLAY = 5;
    const SIXTH_CATEGORY_DISPLAY = 6;

    // Promote the sixth category to be shown in the main nav bar
    if (
      retailCategories.length === SIXTH_CATEGORY_DISPLAY ||
      serviceCategories.length === SIXTH_CATEGORY_DISPLAY
    ) {
      MAX_CATEGORIES_DISPLAY = SIXTH_CATEGORY_DISPLAY;
    }

    if (pathName?.includes(ROUTE_SERVICES)) {
      const renamedCategories = renameUncategorisedCategory(
        serviceCategories,
        t
      );
      setFilteredCategories(
        renamedCategories
          .slice(0, MAX_CATEGORIES_DISPLAY)
          .filter((category: Category) => category.id !== giftCardCategory.id)
      );
      setFilteredMoreCategories(
        renamedCategories
          .slice(MAX_CATEGORIES_DISPLAY)
          .filter((cat: Category) => cat.id !== giftCardCategory.id) || []
      );
    }

    if (pathName?.includes(ROUTE_SHOP)) {
      const renamedCategories = renameUncategorisedCategory(
        retailCategories,
        t
      );
      setFilteredCategories(
        renamedCategories
          .slice(0, MAX_CATEGORIES_DISPLAY)
          .filter((category: Category) => category.id !== giftCardCategory.id)
      );
      setFilteredMoreCategories(
        renamedCategories
          .slice(MAX_CATEGORIES_DISPLAY)
          .filter((cat: Category) => cat.id !== giftCardCategory.id)
      );
    }
  }, [pathName]);

  const handleCategoryClick = (
    e: React.SyntheticEvent,
    categoryName: string
  ) => {
    if (e.nativeEvent instanceof KeyboardEvent) {
      if (e.nativeEvent.key === " ") {
        e.preventDefault();
        globalThis.location.hash = categoryName;
      } else if (e.nativeEvent.key !== "Enter") {
        return;
      }
    }

    const headerId = `${categoryName.replaceAll(/\s+/g, "-")}-header`;
    const headerElement = document.getElementById(headerId);

    if (headerElement) {
      headerElement.scrollIntoView({ behavior: "smooth" });
    }

    setCurrentCategory(categoryName);

    setTimeout(() => {
      if (headerElement) {
        headerElement.focus({ preventScroll: true });
      }
    }, 50);
  };

  return (
    <nav
      className={clsx(
        "hidden",
        "sm:flex",
        "items-center",
        "sm:w-full",
        "sm:pl-10",
        "sm:pr-24",
        "py-6",
        {
          "sm:shadow-[0_4px_10px_0_rgba(0,0,0,0.1)]":
            isScrolled && !isNotAcceptingOrder,
        }
      )}
      style={{ zIndex: 10 }}
      data-testid={applyDataTestId(
        pom.secondaryServicesNavigationBar.container
      )}
      aria-label={t("categoryNavigationLabel")}
    >
      <ol className="flex">
        {filteredCategories.map(
          (c: { id: string; name: string }, index: number) => {
            let queryParams = "";
            if (scanToOrderInfo) {
              queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
            }
            if (menuName) {
              queryParams += queryParams
                ? `&menu=${menuName}`
                : `?menu=${menuName}`;
            }
            const url = `${pathName}${queryParams}#${encodeURIComponent(
              c.name
            )}`;
            return (
              <li key={c.id} className="mr-4 md:mr-9">
                <Link
                  onClick={(e) => handleCategoryClick(e, c.name)}
                  onKeyDown={(e) => handleCategoryClick(e, c.name)}
                  href={url}
                  data-testid={applyDataTestId(
                    pom.landingPage.category(c.name)
                  )}
                  className={clsx(
                    "leading-6 text-gray-900 hover:bg-gray-50 text-base font-normal",
                    {
                      "font-bold underline": currentCategory === c.name,
                    }
                  )}
                  id={
                    index === 0
                      ? pom.accessibilityIds.secondaryNavStart
                      : undefined
                  }
                  aria-label={`${c.name},`}
                >
                  {c.name}
                </Link>
              </li>
            );
          }
        )}
      </ol>

      {filteredMoreCategories.length > 0 && (
        <Popover className="relative">
          {() => (
            <>
              <PopoverButton
                className={clsx(
                  "flex items-center gap-x-2 px-4 border-transparent",
                  "text-base font-normal leading-6 text-gray-900 hover:bg-gray-50",
                  "sm:min-w-[150px] md:min-w-[200px] lg:min-w-[350px]"
                )}
                aria-label={t("viewMoreCategoriesAltText")}
              >
                {t("more")}
                <ChevronDownIcon
                  aria-hidden="true"
                  className="h-5 w-5 flex-none text-gray-900"
                />
              </PopoverButton>
              <PopoverPanel
                transition
                className="absolute -left-8 top-full z-20 mt-3 w-screen sm:max-w-[200px] md:max-w-[250px] lg:max-w-[400px] overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in max-h-[500px] overflow-y-auto p-3"
              >
                <ul className="p-3 list-none">
                  {filteredMoreCategories.map(
                    (c: { id: string; name: string }) => (
                      <li
                        key={c.id}
                        className="group relative flex items-center gap-x-6 rounded-lg p-3 text-sm leading-6 hover:bg-gray-50"
                      >
                        <div className="flex-auto">
                          <Link
                            href={`#${encodeURIComponent(c.name)}`}
                            onClick={(e) => handleCategoryClick(e, c.name)}
                            onKeyDown={(e) => handleCategoryClick(e, c.name)}
                            className="block text-gray-900 font-normal text-base leading-6 text-left"
                            aria-label={`${c.name},`}
                          >
                            {c.name}
                          </Link>
                        </div>
                      </li>
                    )
                  )}
                </ul>
              </PopoverPanel>
            </>
          )}
        </Popover>
      )}
    </nav>
  );
};

const renameUncategorisedCategory = (categories: Category[], t: any) =>
  categories.map((cat) =>
    cat.id === "uncategorised" ? { ...cat, name: t("uncategorized") } : cat
  );
